// Editable header block inside the Receipt Review modal. Lives in
// its own file so `ManufacturingReceiptExtractionModal.tsx` stays
// under the file-size hygiene cap after picking up inline help
// affordances. Renders three editable controls (vendor, shipping,
// tax) plus the date / extracted-at meta. All persistence callbacks
// fire on blur / autocomplete-commit -- the parent owns the actual
// updateManufacturingReceiptHeader call so this component is purely
// presentational + small state.
//
// The InlineHelp popovers below are part of the slice-4h help reorg:
// per-feature copy lives next to the feature, not in the mode-level
// help registry.
import { InlineHelp } from "@/components/common/InlineHelp";
import { ManufacturingVendorAutocomplete } from "./ManufacturingVendorAutocomplete";

type Props = {
  vendorDraft: string;
  vendorSuggestions: string[];
  vendorSavedAt: string | null;
  onVendorChange: (next: string) => void;
  onVendorCommit: (next: string) => void;

  shippingDraft: string;
  onShippingChange: (next: string) => void;
  onShippingBlur: (next: string) => void;

  taxDraft: string;
  onTaxChange: (next: string) => void;
  onTaxBlur: (next: string) => void;

  amountsSavedAt: string | null;
  receiptDate: string;
  extractedAtLabel: string;
};

const amountInputClass =
  "w-20 rounded-md border border-gray-200 bg-white px-2 py-1 text-right text-xs tabular-nums text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";

export function ManufacturingReceiptHeaderEditors(props: Props) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-600 dark:text-zinc-400">
        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-500">
          vendor
        </span>
        <InlineHelp
          label="How vendor + alias learning work"
          dismissKey="mfg.receipts.vendorAlias"
        >
          <strong>Editable.</strong> Type a new name or pick a previously-used vendor
          from the dropdown. When you Post a line whose material you picked manually
          (or where OCR had no suggestion), we silently save the vendor + line
          description → material mapping so the next receipt from this vendor with
          the same line auto-matches.
        </InlineHelp>
        <div className="min-w-[180px] max-w-xs flex-1">
          <ManufacturingVendorAutocomplete
            value={props.vendorDraft}
            vendors={props.vendorSuggestions}
            onChange={props.onVendorChange}
            onCommit={props.onVendorCommit}
            placeholder="(no vendor)"
          />
        </div>
        {props.vendorSavedAt ? (
          <span className="text-[10px] text-emerald-700 dark:text-emerald-300">
            saved
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-600 dark:text-zinc-400">
        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-500">
          shipping
        </span>
        <InlineHelp
          label="Why shipping and tax are editable"
          dismissKey="mfg.receipts.shipTaxEdit"
        >
          OCR commonly mis-reads in-store purchase tax as shipping. These inputs
          default to what was saved at upload time, and auto-fill from OCR when
          those were left at $0. Edit + tab away to save without re-uploading
          the file.
        </InlineHelp>
        <input
          type="text"
          inputMode="decimal"
          value={props.shippingDraft}
          onChange={(e) => props.onShippingChange(e.target.value)}
          onBlur={(e) => props.onShippingBlur(e.target.value)}
          className={amountInputClass}
        />
        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-500">
          tax
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={props.taxDraft}
          onChange={(e) => props.onTaxChange(e.target.value)}
          onBlur={(e) => props.onTaxBlur(e.target.value)}
          className={amountInputClass}
        />
        {props.amountsSavedAt ? (
          <span className="text-[10px] text-emerald-700 dark:text-emerald-300">
            saved
          </span>
        ) : null}
        <span className="text-gray-400 dark:text-zinc-600">·</span>
        <span>{props.receiptDate}</span>
        <span className="text-gray-400 dark:text-zinc-600">·</span>
        <span>{props.extractedAtLabel}</span>
      </div>
    </>
  );
}
